/**
 * A durable queue of writes that have not reached the server yet.
 *
 * Sunday morning is the hard case: a volunteer in a church basement with one
 * bar taps through a roster, and the request fails. The tap must still count.
 * So a submission is written to storage first, sent second, and removed only
 * once the server has acknowledged it — the same order a mail client uses, for
 * the same reason.
 *
 * Storage is a port rather than a dependency: mobile backs it with a file,
 * tests with a Map, and web with nothing at all (the browser is online or the
 * user can see that it is not). Nothing in here imports React or a platform.
 */

export interface QueueStorage {
  read(): Promise<string | null>
  write(value: string): Promise<void>
}

export interface QueuedItem<T> {
  /** UUIDv7, generated on the device — also the server's idempotency key. */
  id: string
  payload: T
  /** ms since epoch, so a queue survives a clock change intact. */
  queuedAt: number
  attempts: number
  lastError?: string
  /**
   * Set when the server refused it for good (a 4xx). The item is kept, not
   * sent again, until a person has seen why and dismissed it.
   */
  rejectedAt?: number
}

/**
 * A queue that never loses an item to a failed send, only to a success — or to
 * a person dismissing a refusal they have read.
 */
export interface OfflineQueue<T> {
  /** Items still waiting to send. */
  list(): Promise<QueuedItem<T>[]>
  /** Items the server refused, with its reason in `lastError`. */
  rejected(): Promise<QueuedItem<T>[]>
  /** A person has dealt with a refused item: forget it. */
  dismiss(id: string): Promise<void>
  enqueue(id: string, payload: T): Promise<QueuedItem<T>>
  /**
   * Sends everything, oldest first, and drops what the server accepted.
   *
   * Returns what happened rather than throwing: a queue flush is background
   * work, and a screen that shows "3 waiting" is a better answer than an error
   * toast about a request the user did not make.
   */
  flush(send: (item: QueuedItem<T>) => Promise<void>): Promise<FlushResult>
  clear(): Promise<void>
}

export interface FlushResult {
  sent: number
  failed: number
  remaining: number
}

/** In-memory, for the web and for tests. Loses everything on reload. */
export function memoryStorage(): QueueStorage {
  let value: string | null = null
  return {
    read: async () => value,
    write: async (next) => {
      value = next
    },
  }
}

/**
 * A send that fails because the *server* rejected the payload — a 4xx that
 * will fail identically forever. Throwing this sets the item aside (see
 * `rejected()`) instead of retrying it until the end of time.
 */
export class PermanentSendError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PermanentSendError'
  }
}

export function createOfflineQueue<T>(storage: QueueStorage): OfflineQueue<T> {
  // Every mutation of the queue file runs through this chain, so two flushes
  // racing on a reconnect cannot interleave a read-modify-write and resurrect
  // an item that was just sent.
  let chain: Promise<unknown> = Promise.resolve()
  const serialize = <R>(work: () => Promise<R>): Promise<R> => {
    const next = chain.then(work, work)
    chain = next.catch(() => undefined)
    return next
  }

  async function read(): Promise<QueuedItem<T>[]> {
    const raw = await storage.read()
    if (!raw) return []
    try {
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as QueuedItem<T>[]) : []
    } catch {
      // A truncated write (the app was killed mid-save) must not brick the
      // queue for good. Losing an unsent submission is bad; refusing to queue
      // anything ever again is worse.
      return []
    }
  }

  const write = (items: QueuedItem<T>[]) => storage.write(JSON.stringify(items))

  return {
    list: () => serialize(async () => (await read()).filter((item) => !item.rejectedAt)),
    rejected: () => serialize(async () => (await read()).filter((item) => item.rejectedAt)),
    dismiss: (id) => serialize(async () => write((await read()).filter((item) => item.id !== id))),

    enqueue: (id, payload) =>
      serialize(async () => {
        const items = await read()
        const existing = items.find((item) => item.id === id)
        if (existing) {
          // Same id, newer payload: the leader corrected themselves before the
          // queue drained. Replace rather than append.
          existing.payload = payload
          existing.attempts = 0
          delete existing.lastError
          await write(items)
          return existing
        }
        const item: QueuedItem<T> = { id, payload, queuedAt: Date.now(), attempts: 0 }
        items.push(item)
        await write(items)
        return item
      }),

    flush: (send) =>
      serialize(async () => {
        const items = await read()
        const waiting = items.filter((item) => !item.rejectedAt)
        if (waiting.length === 0) return { sent: 0, failed: 0, remaining: 0 }

        const keep: QueuedItem<T>[] = items.filter((item) => item.rejectedAt)
        let sent = 0
        let failed = 0
        let remaining = 0

        for (const item of [...waiting].sort((a, b) => a.queuedAt - b.queuedAt)) {
          try {
            await send(item)
            sent += 1
          } catch (error) {
            const lastError = error instanceof Error ? error.message : String(error)
            if (error instanceof PermanentSendError) {
              // The server will never accept this. Set aside, so the queue
              // behind it moves and a person can see what was refused.
              failed += 1
              keep.push({ ...item, attempts: item.attempts + 1, lastError, rejectedAt: Date.now() })
              continue
            }
            // Anything else — offline, a timeout, a 5xx — is kept and retried
            // for as long as it takes. There is no attempt limit: a kiosk
            // retries every few seconds, so any limit is an outage length
            // after which children's check-ins would silently disappear.
            remaining += 1
            keep.push({ ...item, attempts: item.attempts + 1, lastError })
          }
        }

        await write(keep)
        return { sent, failed, remaining }
      }),

    clear: () => serialize(() => write([])),
  }
}
